import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CurrencyInput from 'react-currency-input-field'
import InputField from 'src/components/formik/input-field'
import { SelectField } from 'src/components/formik/select'
import Upload from 'src/components/upload'

import { useNotification } from 'src/contexts/notification.context'
import { convertToFormData, agesData, subjectData } from 'src/tools'
import {
  getPrintableThemes,
  getTopicsRequest,
  deletePrintableFile,
  deletePrintableFiles,
} from 'src/services/api.service'
import CropImage from './components/crop'

const PrintableSchema = Yup.object().shape({
  ages: Yup.array().of(Yup.string()).required('Required').min(1, 'Required'),
  subject_id: Yup.string().nullable('Required').required('Required'),
  themes: Yup.array(),
  topics: Yup.array().of(Yup.number()).nullable(),
  title: Yup.string().required('Required').min(3).max(60),
  description: Yup.string()
    .max(1800, 'Please enter no more than 1800 characters')
    .nullable(),
  sort: Yup.number(),
  price: Yup.string(),
  pages_count: Yup.number().min(0).required(),
  status: Yup.string(),
  practice_url: Yup.string().nullable(),
  // image: Yup.string(),
  // file: [],
  // images: [],
})

const PrintableForm = ({ onSubmit, initialValues }) => {
  const { wrapCallback } = useNotification()
  const [files, setFiles] = useState([])

  const [uploadedCover, setUploadedCover] = useState(null)
  const [cover, setCover] = useState(null)

  const [topics, setTopics] = useState([])
  const [themes, setThemes] = useState([])

  const [errorSize, setErrorSize] = useState(null)

  const handleChangeFiles = (acceptedFiles) => {
    console.log('acceptedFiles', acceptedFiles[0].size)

    setErrorSize(null)

    if (acceptedFiles[0].size > 4 * 1024 * 1024) {
      setErrorSize('File size should not exceed 4 megabytes')
      return
    }

    setFiles([
      ...files,
      ...acceptedFiles.map((file) =>
        Object.assign(file, {
          isUploaded: false,
          preview:
            file.type === 'application/pdf'
              ? '/account/img/pdf.svg'
              : file.type === 'application/zip'
              ? '/account/img/zip.svg'
              : URL.createObjectURL(file),
        }),
      ),
    ])
  }

  console.log('initialValues', initialValues)

  const formik = useFormik({
    initialValues,
    validationSchema: PrintableSchema,
    onSubmit: (data) => {
      const { image, file, ...rest } = data

      const form = convertToFormData(rest)

      if (cover) {
        form.append('image', cover.preview)
      }

      const newFiles = files.filter((item) => !item.isUploaded)

      console.log('newFiles', newFiles)

      if (newFiles?.length > 0) {
        newFiles.forEach((file) => form.append('file[]', file))
      }

      for (const pair of form.entries()) {
        console.log(`${pair[0]}, ${pair[1]}`)
      }
      onSubmit(form, rest.status)
    },
  })

  useEffect(() => {
    wrapCallback(async () => {
      const response = await getPrintableThemes()
      setThemes(response.data?.items || [])
    })
  }, [])

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => URL.revokeObjectURL(file.preview))
    },
    [files],
  )

  useEffect(() => {
    const fetch = async () => {
      if ((formik.values?.ages || [])?.length > 0 && formik.values.subject_id) {
        const result = await getTopicsRequest({
          ages: formik.values.ages,
          subject_id: formik.values.subject_id,
        })
        setTopics(result.data.items || [])
      }
    }
    fetch()
  }, [formik.values.ages, formik.values.subject_id])

  useEffect(() => {
    if ((initialValues?.file || []).length > 0) {
      setFiles(
        initialValues.file.map((file) => ({
          id: null,
          preview: `${process.env.REACT_APP_PRINT_HOST}/${file}`,
          isUploaded: true,
        })),
      )
    }
  }, [initialValues.file])

  useEffect(() => {
    if (!initialValues.image) return

    setUploadedCover({
      preview: `${process.env.REACT_APP_PRINT_HOST}/${initialValues.image}`,
    })
  }, [initialValues.image])

  const handleChangeCover = (url) => {
    setCover({ preview: url })
  }

  const handleApplyCover = (url) => {
    // console.log('handleChangeCover', url);
    // setCover({ preview: url });
  }

  const handleRemoveUploadedCover = (e) => {
    e.preventDefault()
    setUploadedCover(null)
    formik.setFieldValue('image', null)
  }

  const handleRemoveFile = (idx) => (e) => {
    e.preventDefault()

    if (files[idx].isUploaded) {
      wrapCallback(async () => {
        await deletePrintableFile(initialValues.id, idx)
      })
    }

    const newFiles = files.filter((x, i) => i !== idx)
    setFiles(newFiles)
  }

  const handleRemoveAllFile = async (e) => {
    e.preventDefault()
    await deletePrintableFiles(initialValues.id)
    setFiles([])
  }

  const handleSubmitDraft = () => {
    if (formik.isSubmitting) return

    formik.setFieldValue('status', 'draft')
    formik.submitForm()
  }

  const handleSubmitReview = () => {
    if (formik.isSubmitting) return

    formik.setFieldValue('status', 'in review')
    formik.submitForm()
  }

  const thumbs = (
    <div>
      {files.map((file, i) => (
        <div className="thumb" key={file.name}>
          <div className="thumb-inner">
            <img
              src={
                (file.preview || file).indexOf('.pdf') !== -1
                  ? '/account/img/pdf.svg'
                  : (file.preview || file).indexOf('.zip') !== -1
                  ? '/account/img/zip.svg'
                  : file.preview
                  ? file.preview
                  : `${process.env.REACT_APP_PRINT_HOST}/${file}`
              }
              className="img"
            />
            <a onClick={handleRemoveFile(i)} className="remove" href="#">
              <img src="/account/img/remove.svg" alt="" />
            </a>
          </div>
        </div>
      ))}

      {files.length > 0 && (
        <div>
          <a onClick={handleRemoveAllFile} href="#">
            Remove All
          </a>
        </div>
      )}
    </div>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="row">
        <div className="col-lg-3 col-md-4 col-sm-6 pb-30">
          <SelectField
            name="ages"
            label="Age"
            placeholder="Select age"
            options={agesData.map((x) => ({
              label: x.name,
              value: x.id.toString(),
            }))}
            formik={formik}
            isMulti
            closeMenuOnSelect={false}
          />
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6 pb-30">
          <SelectField
            name="subject_id"
            label="Subject"
            placeholder="Select subject"
            options={subjectData.map((x) => ({
              label: x.name,
              value: x.id.toString(),
            }))}
            formik={formik}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6 pb-30">
          <SelectField
            name="topics"
            label="Topics"
            placeholder="Select topics"
            options={topics.map((x) => ({
              label: x.name,
              value: x.id.toString(),
            }))}
            formik={formik}
            isMulti
            closeMenuOnSelect={false}
          />
        </div>

        <div className="col-lg-3 col-md-6 col-sm-6 pb-30">
          <SelectField
            name="themes"
            label="Themes (optional)"
            placeholder="Select theme(s)"
            options={themes.map((x) => ({
              label: x.name,
              value: x.id.toString(),
            }))}
            formik={formik}
            isMulti
            closeMenuOnSelect={false}
          />
        </div>
      </div>
      <div className="input-block">
        <div className="input-top">
          <label htmlFor="q-4">Title</label>
          <div className="tooltip">
            <i className="hb-ico tooltip-ico" />
            <div className="tooltip-info">
              <p>
                Please limit your title tag to 50–60 characters so it display
                properly.{' '}
              </p>
              <p>
                Title tag format: <b>primary Keyword - Secondary Keyword.</b>
              </p>
            </div>
          </div>
        </div>
        <InputField formik={formik} name="title">
          <input
            type="text"
            name="title"
            placeholder="Descriptive title here"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.title}
          />
          <span className="input-hint">
            <b>{formik.values.title?.length}</b>/60 characters
          </span>
        </InputField>
      </div>
      <div className="input-block">
        <div className="input-top">
          <label htmlFor="q-5">Description</label>
          <div className="tooltip tooltip-big">
            <i className="hb-ico tooltip-ico" />
            <div className="tooltip-info">
              <p>Descriptions can be any length between ~80 –160 characters.</p>
              <p>
                It&apos;s best to keep descriptions long enough that
                they&apos;re sufficiently descriptive,These short paragraphs are
                a teacher&apos;s opportunity to promote content to students,
                fellow teachers and parents, so they clearly see whether the
                lesson contains the information they&apos;re searching for.
              </p>
            </div>
          </div>
        </div>
        <InputField formik={formik} name="description">
          <textarea
            name="description"
            placeholder="Please describe what subject/topic your printable worksheets cover"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.description}
          />
          <span className="input-hint">
            <b>{formik.values.description?.length}</b>/1800 characters
          </span>
        </InputField>
      </div>

      <div className="input-block">
        <div className="input-top">
          <label htmlFor="q-5">Link to the Online Practices</label> (optional)
        </div>
        <InputField formik={formik} name="practice_url">
          <input
            type="text"
            name="practice_url"
            placeholder="Please write link"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.practice_url}
          />
        </InputField>
      </div>

      <div>
        <div className="input-block">
          <div className="input-top">
            <label htmlFor="q-6">Cover</label>
            <div className="tooltip tooltip-img">
              <i className="hb-ico tooltip-ico" />
              <div className="tooltip-info">
                <img src="/account/img/tooltip-img-min.png" alt="" />
              </div>
            </div>
          </div>
          {uploadedCover ? (
            <div className="thumb">
              <div className="thumb-inner">
                <img src={uploadedCover.preview} />
                <a
                  onClick={handleRemoveUploadedCover}
                  className="remove"
                  href="#"
                >
                  <img src="/account/img/remove.svg" alt="" />
                </a>
              </div>
            </div>
          ) : (
            <CropImage
              onChange={handleChangeCover}
              onApply={handleApplyCover}
            />
          )}
        </div>
      </div>

      <div className="input-block">
        <div className="input-top">
          <label>Upload files</label>
          <div className="tooltip tooltip-img">
            <i className="hb-ico tooltip-ico" />
            <div className="tooltip-info">
              <img src="/account/img/tooltip-img-2-min.png" alt="" />
            </div>
          </div>
          <span>
            (recommended zip, jpg, png or pdf only,{' '}
            <span className="text-red">Max file size 4MB</span>)
          </span>
        </div>

        <Upload
          accept="zip,application/octet-stream,application/zip,application/x-zip,application/x-zip-compressed,image/*,application/pdf"
          onChange={handleChangeFiles}
        />

        {errorSize && (
          <div>
            <br />
            <b className="text-red">{errorSize}</b>
          </div>
        )}

        <aside className="thumbs-container">{thumbs}</aside>
      </div>
      <div className="row">
        <div className="col-lg-3 col-md-4 col-sm-6">
          <div className="input-block">
            <InputField formik={formik} name="pages_count">
              <label>Number of pages</label>
              <CurrencyInput
                allowNegativeValue={false}
                allowDecimals={false}
                groupSeparator=" "
                name="pages_count"
                maxLength={5}
                placeholder="1"
                onValueChange={(value, name) =>
                  formik.setFieldValue(name, value)
                }
                // onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.pages_count}
              />
            </InputField>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6">
          <div className="input-block">
            <div className="input-top input-top__small">
              <label htmlFor="q-9">Price</label>
              <div className="tooltip">
                <i className="hb-ico tooltip-ico" />
                <div className="tooltip-info">
                  <p>
                    Please limit your title tag to 50–60 characters so it
                    display properly.{' '}
                  </p>
                  <p>
                    Title tag format:{' '}
                    <b>primary Keyword - Secondary Keyword.</b>
                  </p>
                </div>
              </div>
            </div>
            <InputField formik={formik} name="price">
              <CurrencyInput
                allowNegativeValue={false}
                decimalSeparator="."
                groupSeparator=" "
                prefix="$"
                name="price"
                maxLength={5}
                placeholder="0.00"
                decimalsLimit={2}
                decimalScale={2}
                onValueChange={(value, name) =>
                  formik.setFieldValue(name, value)
                }
                // onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.price}
              />
            </InputField>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6" />
        <div className="col-lg-3 col-md-4 col-sm-6">
          <div className="input-block">
            <label>Actual Price</label>
            {Number(formik.values.price || 0) > 0 ? (
              <label style={{ color: '#369CB7' }}>
                ${((Number(formik.values.price || 0) * 8) / 10).toFixed(2)}
              </label>
            ) : (
              <label style={{ color: '#369CB7' }}>FREE</label>
            )}
          </div>
        </div>
      </div>

      {initialValues.status === 'draft' && (
        <div className="worksheetsHb-btn buttons-row">
          <button
            disabled={formik.isSubmitting}
            type="button"
            onClick={handleSubmitDraft}
            className="second-btn"
          >
            {formik.isSubmitting && formik.values.status === 'draft'
              ? 'Saving...'
              : 'Save draft'}
          </button>

          <button
            disabled={formik.isSubmitting}
            type="button"
            onClick={handleSubmitReview}
            className="primary-btn"
          >
            {formik.isSubmitting && formik.values.status === 'in review'
              ? 'Saving...'
              : 'Send to review'}
          </button>
        </div>
      )}
      {initialValues.status === 'in review' && (
        <div className="worksheetsHb-btn buttons-row">
          <button
            disabled={formik.isSubmitting}
            type="button"
            onClick={handleSubmitReview}
            className="primary-btn"
          >
            {formik.isSubmitting && formik.values.status === 'in review'
              ? 'Saving...'
              : 'Save changes'}
          </button>
        </div>
      )}
    </form>
  )
}

export default PrintableForm
